/**
 * Login V2 — SSO + Magic Link
 *
 * Offers Google SSO, Microsoft SSO, and email magic link.
 */

import { useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '@/features/auth/hooks/useSession'
import { env } from '@/lib/env'
import { isNative } from '@/lib/capacitor'
import { setAuthItem } from '@/lib/authStorage'
import { safeParseJson, extractErrorMessage, formatAuthError, formatNetworkError, shouldShowConnectionScreen } from '@/lib/login-helpers'
import { useKeyboardShift } from './useKeyboardShift'
import { ConnectionErrorScreen } from '@/common/components/ConnectionErrorScreen'
import { XaviorLoader } from '@/common/components/XaviorLoader'
import { ImplicitConsentFooter } from '@/features/auth/components/ImplicitConsentFooter'
import { GoogleIcon, MicrosoftIcon } from '@/features/auth/components/ProviderIcons'

const AUTH_BASE_URL = env.authBaseUrl

const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_unsupported: 'This sign-in method is not supported.',
  sso_denied: 'Sign-in was cancelled. Please try again.',
  sso_missing_params: 'Something went wrong with the sign-in. Please try again.',
  sso_invalid_state: 'Sign-in session expired. Please try again.',
  sso_expired: 'Sign-in session expired. Please try again.',
  sso_not_configured: 'This sign-in method is not available.',
  sso_unavailable: 'Sign-in is temporarily unavailable. Please try again.',
  sso_no_email: 'Could not retrieve your email from the provider.',
  sso_account_suspended: 'This account has been suspended. Please contact support.',
  sso_access_denied: 'Access denied. Please contact support.',
  sso_failed: 'Sign-in failed. Please try again.',
}

// Neutral (non-error) status messages surfaced via `?status=` (waitlist gating
// §4.7). Distinct from SSO_ERROR_MESSAGES so the "already on the waitlist" state
// renders calmly, not as a red error. The SSO gate redirects an already-waiting
// user to `/login?status=on_waitlist`.
const STATUS_MESSAGES: Record<string, string> = {
  on_waitlist: "You're already on the waitlist — we'll email you when it's your turn.",
}

export function LoginV2() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setToken, status } = useSession()
  const cardRef = useRef<HTMLDivElement>(null)
  const keyboardStyle = useKeyboardShift(cardRef)

  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info' | ''>('')
  const [connectionError, setConnectionError] = useState<Error | null>(null)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Show SSO error or a neutral status from a redirect. `?error=` renders red;
  // `?status=` (e.g. on_waitlist) renders neutral (waitlist gating §4.7).
  useEffect(() => {
    const ssoError = searchParams.get('error')
    if (ssoError && SSO_ERROR_MESSAGES[ssoError]) {
      setMessage(SSO_ERROR_MESSAGES[ssoError])
      setMessageType('error')
      return
    }
    const status = searchParams.get('status')
    if (status && STATUS_MESSAGES[status]) {
      setMessage(STATUS_MESSAGES[status])
      setMessageType('info')
    }
  }, [searchParams])

  const startResendCountdown = useCallback(() => {
    setResendCountdown(60)
    if (resendTimerRef.current) clearInterval(resendTimerRef.current)
    resendTimerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (resendTimerRef.current) clearInterval(resendTimerRef.current)
          resendTimerRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  useEffect(() => {
    return () => { if (resendTimerRef.current) clearInterval(resendTimerRef.current) }
  }, [])

  useEffect(() => {
    if (status === 'authenticated') {
      // Post-auth → app root (PDR 010). The gate chain (getMainContent → PlanGate
      // → name → app) decides what shows; there is no standalone landing page.
      navigate('/', { replace: true })
    }
  }, [status, navigate])

  const showMessage = (text: string, type: 'success' | 'error' | '' = '') => {
    setMessage(text)
    setMessageType(type)
  }

  const isValidEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
  }

  const handleSso = (provider: string) => {
    // The login provider is recorded server-side at the SSO callback
    // (users.login_provider, surfaced as profile.loginProvider, PDR 010 D3) — no
    // browser stash needed; the post-plan connect reads it to target the account.
    //
    // Inside the Capacitor app, OAuth runs in the external browser and a
    // universal-link redirect can't bounce back; pass platform=capacitor so the
    // callback returns via the xavior:// custom scheme instead (web is untouched —
    // isNative() is only true in the native shell).
    const url = `${AUTH_BASE_URL}/auth/sso/${provider}${isNative() ? '?platform=capacitor' : ''}`
    if (isNative()) {
      // Native (Capacitor iOS): open SSO in the EXTERNAL system browser instead
      // of navigating the WebView. window.location.href would navigate the WebView
      // off the React app — tearing down DeepLinkHandler's appUrlOpen listener — so
      // the xavior://auth/verify return is dropped and sign-in silently fails (the
      // magic link works precisely because it never leaves the WebView). window.open
      // '_blank' routes through UIApplication.open → full Safari, which DOES bounce
      // the xavior:// custom scheme back into the still-alive app, where the live
      // listener catches it. sprint native-oauth-pass Ch.3.
      window.open(url, '_blank', 'noopener')
      return
    }
    window.location.href = url
  }

  const handleSendMagicLink = async () => {
    if (!email || !isValidEmail(email)) {
      showMessage('Please enter a valid email address (e.g. name@example.com)', 'error')
      return
    }

    setIsLoading(true)
    setConnectionError(null)

    try {
      const url = `${AUTH_BASE_URL}/auth/request-access`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include',
      })

      const responseText = await response.text()
      const responseData = responseText ? safeParseJson(responseText) : null

      if (!response.ok) {
        if (shouldShowConnectionScreen(response.status)) {
          const detail = extractErrorMessage(responseData) || response.statusText || 'Login service unavailable'
          setConnectionError(new Error(`HTTP ${response.status}: ${detail}`))
          setIsLoading(false)
          return
        }
        throw new Error(formatAuthError(response.status, responseData))
      }

      if (!responseData || typeof responseData !== 'object') {
        throw new Error('Unexpected response from login service.')
      }

      const data = responseData as {
        autoLogin?: boolean
        accessToken?: string
        jwt?: string
        sessionToken?: string
        email?: string
        userId?: string
        waitlisted?: boolean
        waitlistUrl?: string
        alreadyOnWaitlist?: boolean
      }

      // INVITE_ONLY (waitlist gating §4.7). 3-way: an email ALREADY on the
      // in-app waitlist queue shows a neutral in-app status instead of being
      // bounced to the signup page; a not-yet-listed email keeps the redirect.
      if (data.waitlisted && data.alreadyOnWaitlist) {
        setMessage(STATUS_MESSAGES.on_waitlist)
        setMessageType('info')
        setIsLoading(false)
        return
      }
      if (data.waitlisted && data.waitlistUrl) {
        window.location.href = data.waitlistUrl
        return
      }

      if (data.autoLogin && (data.accessToken || data.jwt || data.sessionToken)) {
        const authToken = data.accessToken || data.jwt || data.sessionToken
        // Persist identity via setAuthItem so these land in the platform-canonical
        // store (localStorage on native, sessionStorage on web), matching
        // MagicLinkVerify. Raw sessionStorage dropped userId/userEmail/sessionToken
        // on native (the reader now reads localStorage), so apiClient's
        // X-User-Email header went missing until the next refresh.
        if (authToken) setAuthItem('xavior_auth_token', authToken)
        if (data.sessionToken) setAuthItem('sessionToken', data.sessionToken)
        if (data.userId) setAuthItem('userId', data.userId)
        if (data.email) setAuthItem('userEmail', data.email)
        setToken(authToken)
        setIsLoading(false)
        navigate('/', { replace: true })
        return
      }

      setMagicLinkSent(true)
      startResendCountdown()
      setIsLoading(false)

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('[LoginV2] Error:', errorMessage)

      if (shouldShowConnectionScreen(undefined, error)) {
        const message = formatNetworkError(error)
        setConnectionError(error instanceof Error ? error : new Error(message))
        setIsLoading(false)
        return
      }
      showMessage(formatNetworkError(error), 'error')
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSendMagicLink()
  }

  // ---- Render: Connection Error ----

  if (connectionError) {
    return (
      <ConnectionErrorScreen
        error={connectionError}
        onRetry={() => {
          setConnectionError(null)
          if (email) void handleSendMagicLink()
        }}
      />
    )
  }

  // ---- Render: Checking Auth ----

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#f8f7f5' }}>
        <div className="bg-white rounded-2xl p-10 max-w-md w-full border" style={{ maxWidth: '420px', borderColor: '#e8e6e2' }}>
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
                <span className="text-white text-sm font-bold">X</span>
              </div>
              <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
            </div>
          </div>
          <div className="text-center">
            <XaviorLoader size="lg" className="mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Restoring session...</p>
          </div>
        </div>
      </div>
    )
  }

  // ---- Render: Magic Link Sent ----

  if (magicLinkSent) {
    return (
      <div className="login-page min-h-[100dvh] flex items-center justify-center p-5 overflow-x-hidden transition-all duration-300" style={{ background: '#f8f7f5' }}>
        <div className="bg-white rounded-2xl p-10 max-w-md w-full border" style={{ maxWidth: '420px', borderColor: '#e8e6e2' }}>
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
                <span className="text-white text-sm font-bold">X</span>
              </div>
              <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
            </div>
          </div>

          <div className="text-center mb-6">
            <div className="text-4xl mb-4">&#9993;</div>
            <h2 className="text-2xl font-normal text-gray-900 mb-2">Check your email</h2>
            <p className="text-gray-400 text-sm mb-1">We sent a magic link to</p>
            <p className="text-gray-900 font-medium text-sm">{email}</p>
          </div>

          <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 text-center mb-6">
            <p className="text-amber-700 text-xs">Don't see it? Check your spam or promotions folder.</p>
          </div>

          <button
            onClick={() => {
              if (resendCountdown > 0) return
              void handleSendMagicLink()
              startResendCountdown()
            }}
            disabled={resendCountdown > 0 || isLoading}
            className="w-full py-3 px-6 bg-gray-50 hover:bg-gray-100 text-gray-600 font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-default text-sm border"
            style={{ borderColor: '#e8e6e2' }}
          >
            {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend magic link'}
          </button>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => { setMagicLinkSent(false); setMessage(''); setMessageType('') }}
              className="text-sm hover:underline bg-transparent border-none cursor-pointer"
              style={{ color: '#0ea5e9' }}
            >
              Use a different method
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ---- Render: Main Login ----

  return (
    <div className="login-page min-h-[100dvh] flex items-center justify-center p-5 overflow-x-hidden transition-all duration-300" style={{ background: '#f8f7f5' }}>
      <div ref={cardRef} className="bg-white rounded-2xl p-10 max-w-md w-full border" style={{ maxWidth: '420px', borderColor: '#e8e6e2', ...keyboardStyle }}>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
              <span className="text-white text-sm font-bold">X</span>
            </div>
            <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
          </div>
        </div>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-normal text-gray-900 mb-2">Sign in or create account</h2>
          <p className="text-gray-400 text-sm">Choose your preferred sign-in method</p>
        </div>

        <button
          onClick={() => handleSso('google')}
          disabled={isLoading}
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl text-sm border flex items-center justify-center gap-3 mb-3 disabled:opacity-50 disabled:cursor-default"
          style={{ borderColor: '#e8e6e2', transition: 'background 0.15s ease' }}
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <button
          onClick={() => handleSso('microsoft')}
          disabled={isLoading}
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl text-sm border flex items-center justify-center gap-3 mb-6 disabled:opacity-50 disabled:cursor-default"
          style={{ borderColor: '#e8e6e2', transition: 'background 0.15s ease' }}
        >
          <MicrosoftIcon />
          Continue with Microsoft
        </button>

        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px" style={{ background: '#e8e6e2' }} />
          <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px" style={{ background: '#e8e6e2' }} />
        </div>

        <input
          type="email"
          name="email"
          id="email"
          autoComplete="email"
          enterKeyHint="send"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your email"
          disabled={isLoading}
          className="w-full py-3.5 px-4 text-sm border rounded-xl mb-3 bg-white text-gray-900 focus:outline-none focus:ring-2 disabled:opacity-50"
          style={{ fontSize: '16px', borderColor: '#e8e6e2', transition: 'border-color 0.2s, box-shadow 0.2s' }}
          onFocus={(e) => { e.target.style.borderColor = '#0ea5e9'; e.target.style.boxShadow = '0 0 0 3px rgba(14,165,233,0.12)' }}
          onBlur={(e) => { e.target.style.borderColor = '#e8e6e2'; e.target.style.boxShadow = 'none' }}
        />

        <p className="text-gray-400 text-xs text-center mb-3">We'll send you a magic link — no password needed</p>

        <button
          onClick={handleSendMagicLink}
          disabled={isLoading}
          className="w-full py-3.5 px-6 text-white font-medium rounded-xl disabled:opacity-50 text-sm"
          style={{ background: '#0ea5e9', transition: 'background 0.15s ease' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#0284c7' }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#0ea5e9' }}
        >
          {isLoading ? 'Sending...' : 'Continue with email'}
        </button>

        <div className="flex justify-center mt-5">
          <span className="text-xs text-gray-400">Free to start · No credit card required</span>
        </div>

        <div className="h-5 mt-3">
          {message && (
            <p className={`text-center text-sm ${
              messageType === 'success' ? 'text-[#0ea5e9]' :
              messageType === 'error' ? 'text-[var(--color-danger)]' :
              'text-gray-600'
            }`}>
              {message}
            </p>
          )}
        </div>

        {/* Implicit-consent footer — launch T&C mechanism (PDR 011 D8, #4224).
            Informational only; does not gate the login buttons above. */}
        <ImplicitConsentFooter />
      </div>
    </div>
  )
}
